import React from 'react';
import PropTypes from 'prop-types';

const DoctorListings = ({ doctors, pageTitle = "Book an Appointment", breadcrumb = [] }) => {
  return (
    <section className="py-14 md:py-20 lg:py-16 bg-gray-50">
      <div className="container mx-auto px-4 md:px-8 max-w-6xl">
        {/* Breadcrumb/Header */}
        <div className="mb-6 md:mb-8">
          {breadcrumb.length > 0 && (
            <nav className="flex items-center text-sm mb-4 text-gray-600">
              {breadcrumb.map((item, index) => (
                <div key={index} className="flex items-center">
                  <a 
                    href={item.link} 
                    className={`hover:text-primary-color transition-colors ${
                      index === breadcrumb.length - 1 ? 'text-primary-color font-semibold' : ''
                    }`}
                  >
                    {item.label}
                  </a>
                  {index < breadcrumb.length - 1 && (
                    <span className="mx-2">/</span>
                  )}
                </div>
              ))}
            </nav>
          )}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold primary-text">
            {pageTitle}
          </h1>
        </div>

        {/* Doctor Cards */}
        <div className="space-y-6">
          {doctors.map((doctor, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-5 md:p-6"
            >
              <div className="flex flex-col md:flex-row gap-6">
                {/* Doctor Image */}
                <div className="flex-shrink-0">
                  <img
                    src={doctor.image}
                    alt={doctor.name}
                    className="w-32 h-32 md:w-36 md:h-36 rounded-lg object-cover"
                  />
                </div>

                {/* Doctor Info */}
                <div className="flex-grow">
                  {/* Name and Specialty */}
                  <h2 className="text-xl md:text-2xl font-bold primary-text mb-1">
                    {doctor.name}
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base mb-2">
                    {doctor.specialty}
                  </p>

                  {/* Location */}
                  <div className="flex items-start gap-2 text-gray-600 mb-4">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm md:text-base">{doctor.location}</span>
                  </div>

                  {/* Experience, Ratings, Availability */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-xs md:text-sm text-gray-500 font-semibold mb-1">Experience</p>
                      <p className="text-sm md:text-base font-medium text-gray-800">{doctor.experience}</p>
                    </div>
                    <div>
                      <p className="text-xs md:text-sm text-gray-500 font-semibold mb-1">Ratings</p>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4 md:w-5 md:h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-sm md:text-base font-medium text-gray-800">{doctor.rating}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs md:text-sm text-gray-500 font-semibold mb-1">Availability</p>
                      <p className={`text-sm md:text-base font-medium ${
                        doctor.isAvailable ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {doctor.availability}
                      </p>
                      {doctor.availableUntil && (
                        <p className="text-xs text-gray-500">{doctor.availableUntil}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Side - Actions */}
                <div className="flex flex-col gap-3 md:ml-auto flex-shrink-0">
                  {/* Book/Check Button */}
                  <button
                    onClick={() => doctor.onBook && doctor.onBook()}
                    className={`${
                      doctor.isAvailable ? 'primary-bg' : 'bg-gray-600'
                    } text-white px-6 py-2.5 rounded-lg font-semibold hover:opacity-90 transition-all duration-300 flex items-center gap-2 justify-center whitespace-nowrap`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {doctor.isAvailable ? 'Book An Appointment' : 'Check Availability'}
                  </button>

                  {/* Distance and Directions */}
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-600">{doctor.distance}</span>
                    <a
                      href={doctor.directionsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Get Directions
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

DoctorListings.propTypes = {
  doctors: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      specialty: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      experience: PropTypes.string.isRequired,
      rating: PropTypes.string.isRequired,
      availability: PropTypes.string.isRequired,
      availableUntil: PropTypes.string,
      isAvailable: PropTypes.bool.isRequired,
      distance: PropTypes.string.isRequired,
      directionsLink: PropTypes.string.isRequired,
      onBook: PropTypes.func,
    })
  ).isRequired,
  pageTitle: PropTypes.string,
  breadcrumb: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
    })
  ),
};

export default DoctorListings;